import {
	datetime_interpretations,
	datetime_format_only,
} from "./types/datetime.js";
import { number_interpretations } from "./types/number.js";
import { string_interpretation } from "./types/string.js";

var INTERPRETATION_OPTIONS = Object.freeze({
	datetime: datetime_interpretations,
	number: number_interpretations,
});

var DEFAULT_INTERPRETATIONS_ARRAY = Object.freeze([
	"datetime",
	"number",
	"string",
]);

var DEFAULT_OPTIONS = Object.freeze({
	n_max: 250,
	n_failing_values: 0,
	failure_fraction: 5 / 100,
	sort: true,
});

var OPTION_KEYS = Object.freeze(Object.keys(DEFAULT_OPTIONS));

function snakeToCamel(snake_string) {
	return snake_string.replace(/_(\w)/g, function (match, capture) {
		return capture.toUpperCase();
	});
}

function noSort(a, b) {
	return a.index - b.index;
}
function sortBySuccess(a, b) {
	return b.n_success - a.n_success || noSort(a, b);
}

function trim(value) {
	return ("" + value).trim();
}

function createAccessorFunction(accessor) {
	if (accessor === undefined)
		return function (value) {
			return trim(value);
		};
	if (typeof accessor === "function")
		return function (value, index) {
			return trim(accessor(value, index));
		};
	return function (value) {
		return trim(value["" + accessor]);
	};
}

export function createInterpreter(interpretations_array) {
	if (!interpretations_array)
		interpretations_array = DEFAULT_INTERPRETATIONS_ARRAY;
	else if (!Array.isArray(interpretations_array))
		interpretations_array = [interpretations_array];

	var interpretations = interpretations_array.reduce(function (
		arr,
		interp_string,
	) {
		var interps = INTERPRETATION_OPTIONS[interp_string];
		if (interps) Array.prototype.push.apply(arr, interps);
		return arr;
	}, []);

	var include_string = interpretations_array.indexOf("string") !== -1;

	var options = OPTION_KEYS.reduce(function (obj, key) {
		obj[key] = DEFAULT_OPTIONS[key];
		return obj;
	}, {});

	var interpreter = function (input_array, accessor) {
		accessor = createAccessorFunction(accessor);
		var data = input_array.map(accessor).filter(Boolean);
		if (!data.length) return include_string ? [string_interpretation] : [];
		var n = Math.min(options.n_max, data.length);
		var n_max_failure = Math.floor(n * options.failure_fraction);
		var n_failing_values = options.n_failing_values;
		var sortMethod = options.sort ? sortBySuccess : noSort;

		var valid_interpreters = interpretations
			.slice()
			.reduce(function (keep, interp, index) {
				var n_fail = (i = 0);
				var failing_values = [];
				var complete_failure = false;

				for (var i = 0; i < n; i++) {
					var val = data[i];
					var is_valid = interp.test(val);
					if (is_valid) continue;
					if (++n_fail > n_max_failure) complete_failure = true;
					else if (failing_values.indexOf(val) === -1) {
						failing_values.push(val);
						if (failing_values.length > n_failing_values)
							complete_failure = true;
					}
					if (complete_failure) break;
				}

				if (!complete_failure)
					keep.push({ interp: interp, n_success: n - n_fail, index: index });

				return keep;
			}, [])
			.sort(sortMethod)
			.map(function (valid) {
				return valid.interp;
			});

		if (include_string) valid_interpreters.push(string_interpretation);

		return valid_interpreters;
	};

	OPTION_KEYS.forEach(function (option) {
		interpreter[snakeToCamel(option)] = function (value) {
			if (value === undefined) return options[option];
			options[option] = value;
			return interpreter;
		};
	});

	return interpreter;
}

createInterpreter.DATETIME_IDS = Object.freeze(
	datetime_interpretations.concat(datetime_format_only).map(function (d) {
		return d.id;
	}),
);
createInterpreter.NUMBER_IDS = Object.freeze(
	number_interpretations.map(function (d) {
		return d.id;
	}),
);
createInterpreter.STRING_IDS = Object.freeze([string_interpretation.id]);
createInterpreter.DATETIME_FORMAT_ONLY_IDS = Object.freeze(
	datetime_format_only.map(function (d) {
		return d.id;
	}),
);

createInterpreter.getInterpretation = (function () {
	var interpretations = datetime_interpretations.concat(
		datetime_format_only,
		number_interpretations,
		string_interpretation,
	);
	var lookup = interpretations.reduce(function (l, d) {
		l[d.id] = d;
		return l;
	}, {});
	return function (id) {
		return lookup[id];
	};
})();

createInterpreter._createAccessorFunction = createAccessorFunction;

export var recognised_formats = INTERPRETATION_OPTIONS;
